import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import {
  desplazarMes, mesActual, nombreDelMes, TIPO_AUSENCIA_COLOR, TIPO_AUSENCIA_LABEL,
  type Ausencia, type FilaAusencias, type TipoAusencia,
} from '@/lib/personal';

/**
 * Quién falta cada día del mes.
 *
 * La cuadrícula existe para una pregunta muy concreta: si dos personas de la
 * misma zona piden la misma semana de agosto, hay que verlo ANTES de decir que
 * sí. En una lista por persona eso no se ve.
 *
 * Los días fuera de alta (antes de entrar, después de irse) van en gris para
 * que no parezcan huecos disponibles.
 */
export default function PersonalAusenciasPage() {
  const [params, setParams] = useSearchParams();
  const mes = params.get('mes') || mesActual();
  const [detalle, setDetalle] = useState<{ nombre: string; ausencia: Ausencia } | null>(null);

  const ponerMes = (m: string) => {
    const siguiente = new URLSearchParams(params);
    siguiente.set('mes', m);
    setParams(siguiente, { replace: true });
  };

  const { data, loading, error } = useApi<{ mes: string; desde: string; hasta: string; empleados: FilaAusencias[] }>(
    () => api.getAusenciasEquipo(mes),
    [mes],
  );

  const dias = useMemo(() => {
    if (!data) return [];
    const total = Number(data.hasta.slice(8, 10));
    return Array.from({ length: total }, (_, i) => `${mes}-${String(i + 1).padStart(2, '0')}`);
  }, [data, mes]);

  const hoy = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{nombreDelMes(mes)}</h2>
        <div className="flex items-center gap-1">
          <button onClick={() => ponerMes(desplazarMes(mes, -1))} className="btn-secondary px-2.5 py-1 text-sm">←</button>
          <button onClick={() => ponerMes(mesActual())} className="btn-secondary px-2.5 py-1 text-sm">Hoy</button>
          <button onClick={() => ponerMes(desplazarMes(mes, 1))} className="btn-secondary px-2.5 py-1 text-sm">→</button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <EmptyState title="No se han podido cargar las ausencias" description={error} />
      ) : !data || data.empleados.length === 0 ? (
        <EmptyState title="No hay nadie en plantilla este mes" />
      ) : (
        <>
          <div className="card p-0 overflow-x-auto">
            <table className="border-collapse">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-white text-left text-xs font-semibold text-sereno-dark/50 uppercase px-3 py-2 min-w-[150px]">
                    Persona
                  </th>
                  {dias.map((d) => {
                    const diaSemana = new Date(`${d}T12:00:00Z`).getUTCDay();
                    const finde = diaSemana === 0 || diaSemana === 6;
                    return (
                      <th
                        key={d}
                        className={`text-[10px] font-medium w-6 py-2 ${
                          d === hoy ? 'text-sereno-dark font-bold' : finde ? 'text-sereno-dark/25' : 'text-sereno-dark/45'
                        }`}
                      >
                        {Number(d.slice(8, 10))}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.empleados.map((emp) => (
                  <tr key={emp.id} className="border-t border-gray-50">
                    <td className="sticky left-0 z-10 bg-white px-3 py-1.5">
                      <Link
                        to={`/equipo/personal/${emp.id}?tab=ausencias`}
                        className="text-sm hover:underline truncate block max-w-[150px]"
                      >
                        {emp.nombreCompleto}
                      </Link>
                    </td>
                    {dias.map((d) => {
                      const fueraDeAlta =
                        (emp.fechaAlta && d < emp.fechaAlta) ||
                        (emp.situacion === 'baja' && emp.fechaBaja && d > emp.fechaBaja);
                      const a = emp.ausencias.find((x) => x.desde <= d && d <= x.hasta);
                      const descansa = !a && emp.descansos.includes(d);
                      return (
                        <td key={d} className="p-0.5">
                          <button
                            type="button"
                            disabled={!a}
                            onClick={() => a && setDetalle({ nombre: emp.nombreCompleto, ausencia: a })}
                            title={
                              a
                                ? `${TIPO_AUSENCIA_LABEL[a.tipo]}: ${a.desde} — ${a.hasta}`
                                : descansa
                                  ? 'Día libre del cuadrante de la lavandería'
                                  : fueraDeAlta
                                    ? 'Fuera de alta'
                                    : ''
                            }
                            className={`block w-5 h-5 rounded-sm ${
                              a
                                ? `${TIPO_AUSENCIA_COLOR[a.tipo]} cursor-pointer hover:opacity-80`
                                : descansa
                                  ? 'bg-gray-300'
                                  : fueraDeAlta
                                    ? 'bg-gray-100'
                                    : 'bg-gray-50'
                            } ${d === hoy ? 'ring-1 ring-sereno-dark/40' : ''}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Leyenda */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-sereno-dark/50">
            {(Object.keys(TIPO_AUSENCIA_LABEL) as TipoAusencia[]).map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5">
                <span className={`w-3 h-3 rounded-sm ${TIPO_AUSENCIA_COLOR[t]}`} />
                {TIPO_AUSENCIA_LABEL[t]}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-gray-300" />
              Descanso del cuadrante
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-gray-100" />
              Fuera de alta
            </span>
          </div>

          {detalle && (
            <div className="card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-sm">{detalle.nombre}</p>
                  <p className="text-sm text-sereno-dark/60">
                    {TIPO_AUSENCIA_LABEL[detalle.ausencia.tipo]} · {detalle.ausencia.desde} — {detalle.ausencia.hasta} ·{' '}
                    {detalle.ausencia.diasNaturales} días
                  </p>
                  {detalle.ausencia.nota && (
                    <p className="text-sm text-sereno-dark/50 mt-1">{detalle.ausencia.nota}</p>
                  )}
                </div>
                <button onClick={() => setDetalle(null)} className="text-sm text-sereno-dark/40 hover:text-sereno-dark">
                  Cerrar
                </button>
              </div>
            </div>
          )}

          <p className="text-[11px] text-sereno-dark/35">
            Las ausencias se dan de alta desde la ficha de cada persona. Los descansos en gris vienen del cuadrante
            de la app de lavandería y aquí solo se leen: se pintan para no aprobar vacaciones encima de un día
            libre ya dado.
          </p>
        </>
      )}
    </div>
  );
}
